import {
  Button,
  IconButton,
  Stack,
  TableCell,
  Typography,
  Select,
  MenuItem,
  Chip,
  Avatar,
} from '@mui/material'
import { User } from 'types'
import DeleteIcon from '@mui/icons-material/Delete'

const STATUS_OPTIONS = ['Not Started', 'In Progress', 'Not Applicable', 'Done'] as const

interface DraftRowsProps {
  currentTeam: User[]
  draft: any
  patchDraftRow: (itemId: string, patch: Partial<any>) => void
  saveDraftRow: (draft: any) => void
  discardDraftRow: (draft: any) => void
}

export const DraftRows = ({ currentTeam, draft, patchDraftRow, saveDraftRow, discardDraftRow }: DraftRowsProps) => {
  return (
    // <TableRow key={`draft_${draft.itemId}`} hover>
    <>
      <TableCell>{draft.itemIndex}</TableCell>

      {/* Task name input */}
      <TableCell>
        <input
          style={{ width: '100%', padding: 6 }}
          value={draft.name}
          onChange={e => patchDraftRow(draft.itemId, { name: e.target.value })}
          placeholder="Task name…"
        />
      </TableCell>

      {/* Role select */}
      <TableCell>
        <Select
          size="small"
          fullWidth
          value={draft.role || ''}
          onChange={e => patchDraftRow(draft.itemId, { role: String(e.target.value) })}
        >
          <MenuItem value="">
            <em>—</em>
          </MenuItem>
          {['AM', 'PM', 'DMS', 'SEO', 'QA', 'BD', 'MIA', 'Integration'].map(r => (
            <MenuItem key={r} value={r}>
              {r}
            </MenuItem>
          ))}
        </Select>
      </TableCell>

      {/* Assignees (from current team, filtered by role) */}
      <TableCell>
        <Select
          size="small"
          fullWidth
          multiple
          displayEmpty
          value={draft.assigneeIds ?? []}
          onChange={e => {
            const newIds = Array.from(new Set((e.target.value as string[]) ?? []))
            patchDraftRow(draft.itemId, { assigneeIds: newIds })
          }}
          renderValue={selected => {
            const ids = selected as string[]
            if (!ids.length) return <em>Unassigned</em>
            return (
              <Stack direction="row" spacing={0.5} flexWrap="wrap">
                {ids.map(id => {
                  const u = currentTeam?.find((t: any) => (t.user_uuid || t.id) === id)
                  return (
                    <Chip
                      key={id}
                      size="small"
                      avatar={
                        <Avatar src={u?.image_512} sx={{ width: 24, height: 24 }}>
                          {u?.name?.[0] ?? '?'}
                        </Avatar>
                      }
                      label={u?.name ?? id}
                    />
                  )
                })}
              </Stack>
            )
          }}
        >
          <MenuItem disabled value="">
            <em>Select assignees…</em>
          </MenuItem>
          {(draft.role ? currentTeam.filter((u: any) => (u.role ?? u.slackRole) === draft.role) : currentTeam).map(
            (u: any) => {
              const id = u.user_uuid || u.id
              return (
                <MenuItem key={id} value={id}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar src={u.image_512} sx={{ width: 24, height: 24 }}>
                      {u.name?.[0] ?? id[0]}
                    </Avatar>
                    <Typography variant="body2">{u.name}</Typography>
                  </Stack>
                </MenuItem>
              )
            }
          )}
        </Select>
      </TableCell>

      {/* Status */}
      <TableCell>
        <Select
          size="small"
          fullWidth
          value={STATUS_OPTIONS.includes(draft.status) ? draft.status : 'Not Started'}
          onChange={e => patchDraftRow(draft.itemId, { status: String(e.target.value) })}
        >
          {STATUS_OPTIONS.map(s => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </Select>
      </TableCell>

      {/* Actions: Save / Discard */}
      <TableCell align="right">
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            size="small"
            variant="contained"
            onClick={() => saveDraftRow(draft)}
            disabled={!draft.name?.trim()} // require a name
          >
            Save
          </Button>
          <IconButton size="small" color="error" onClick={() => discardDraftRow(draft)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      </TableCell>
    </>
  )
}
